import React, { useState, useEffect, useCallback } from 'react'
import { JournalCard, JournalMainCard } from '~pages/journals-page/JournalCard'
import { getFacultiesData } from '~shared/api/faculties/getFacultiesData'
import { getJournalsData } from '~shared/api/getJournalsData/getJournalsData'
import { TJournal } from '~shared/api/getJournalsData/types'
import { cn } from '~shared/utils/cn'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { getJournalPath } from '~shared/utils/getJournalPath'

const INITIAL_SIZE = 11
const PAGE_SIZE = 10
const context = { locale: 'ru' } as any

type TJournalsSectionProps = {
  faculties: Awaited<ReturnType<typeof getFacultiesData>>
  journals: Awaited<ReturnType<typeof getJournalsData>>
}

const Chip = ({
  className,
  name,
  active,
  onClick,
  style,
  href
}: {
  name: string
  active?: boolean
  onClick?: () => void
  className?: string
  style?: React.CSSProperties
  href?: string
}) => {
  const baseClass = cn(
    'cursor-pointer transition-all text-[14px] duration-300 relative h-[37px] px-[16px] flex items-center justify-center rounded-[8px] border-[1px]',
    active && 'text-white',
    className
  )

  if (href) {
    return (
      <Link href={href} className={baseClass} style={style}>
        {name}
      </Link>
    )
  }

  return (
    <button onClick={onClick} className={baseClass} style={style}>
      {name}
    </button>
  )
}

export const JournalsSection = ({
  faculties,
  journals
}: TJournalsSectionProps) => {
  const pathname = usePathname()

  // Определяем activeFilter из pathname
  const getActiveFilter = () => {
    if (pathname === '/journal') return undefined
    const match = pathname?.match(/^\/journal(?:--|\/)(.+)$/)
    return match ? match[1] : undefined
  }

  const activeFilter = getActiveFilter()

  const [stateJournals, setJournals] = useState<TJournal[]>(
    (journals?.data as TJournal[]) ?? []
  )
  const [offset, setOffset] = useState(0)

  const [hasMore, setHasMore] = useState(
    (journals?.data?.length ?? 0) > INITIAL_SIZE
  )
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    const data = (journals as any)?.data ?? journals
    const total = (journals as any)?.total ?? 0
    setJournals((data as TJournal[]) ?? [])
    setHasMore(total > INITIAL_SIZE)
    setOffset(INITIAL_SIZE)
    setLoading(false)
  }, [pathname, journals])

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return

    setLoading(true)

    try {
      const res = await getJournalsData({
        context,
        filterBy: activeFilter,
        start: offset,
        amount: PAGE_SIZE
      })

      // если уже другой роут/запрос — игнорируем
      const newItems = ((res as any)?.data as TJournal[]) ?? []
      const total = (res as any)?.total ?? 0
      setJournals(prev => [...prev, ...newItems])

      setOffset(prev => prev + newItems.length)
      setHasMore(offset + newItems.length < total)
    } finally {
      setLoading(false)
    }
  }, [offset, loading, hasMore, activeFilter])

  const chipBase = 'flex-shrink-0 md:flex-shrink-[1] md:flex-grow-0'
  // console.log('stateJournals', stateJournals)
  // console.log('journals', journals)
  console.log('hasMore', hasMore)
  return (
    <section className='pb-[40px]'>
      <div className='mx-auto px-[16px] box-border max-w-[672px] md:max-w-[832px] 2lg:max-w-[1280px]'>
        <div
          className={cn(
            'mt-[30px] md:mt-[24px] lg:mt-[40px] flex overflow-x-auto overflow-y-hidden gap-[8px] whitespace-nowrap',
            'md:flex-wrap',
            'xl:gap-[16px] xl:mt-[60px] xl:max-w-[1000px]'
          )}>
          <Chip
            name='Все статьи'
            href='/journal'
            active={activeFilter === undefined}
            className={cn(
              'border-accent',
              activeFilter === undefined
                ? 'bg-accent'
                : 'hover:bg-accent hover:text-white'
            )}
          />
          {faculties?.map(faculty => (
            <Chip
              key={faculty.slug!}
              name={faculty.name!}
              href={getJournalPath(faculty.slug)}
              active={activeFilter === faculty.slug}
              style={{
                borderColor: faculty.color!,
                ...(activeFilter === faculty.slug
                  ? { backgroundColor: faculty.color! }
                  : ({ '--color': faculty.color! } as any))
              }}
              className={cn(
                'hover:bg-[var(--color)] hover:text-white',
                chipBase
              )}
            />
          ))}
        </div>

        <div className='mt-[30px] md:mt-[40px] xl:mt-[60px]'>
          {loading && stateJournals.length === 0 ? (
            <p className='text-center text-[#6A6A6A]'>Загрузка...</p>
          ) : stateJournals.length === 0 ? (
            <p className='text-center text-[#6A6A6A]'>Статьи не найдены</p>
          ) : (
            <>
              {/* Первая статья — большая */}
              <JournalMainCard model={stateJournals[0]} />

              {/* Остальные — плитка */}
              <div className='flex flex-wrap justify-between'>
                {stateJournals.slice(1).map((journal, idx) => {
                  // idx — 0-based индекс среди карточек (без первой большой)
                  // Паттерн Desktop (5): [narrow, narrow, narrow, wide, wide, narrow, narrow, narrow, wide, wide, ...]
                  // pos внутри пятёрки: 0,1,2 -> narrow; 3,4 -> wide
                  const posInFive = idx % 5
                  const isDesktopWide = posInFive === 3 || posInFive === 4

                  // Паттерн Tablet (3): каждая 3я (idx=2,5,8...) — полная ширина
                  const posInThree = idx % 3
                  const isTabletFull = posInThree === 2

                  return (
                    <JournalCard
                      key={journal.slug}
                      model={journal}
                      className={cn(
                        'mt-[32px] xl:mt-[48px] w-full',
                        isTabletFull ? 'md:w-full' : 'md:w-[48%]',
                        isDesktopWide ? '2lg:w-[48.5%]' : '2lg:w-[31.5%]'
                      )}
                    />
                  )
                })}
                <div className='2lg:w-[31.5%]' />
                <div className='2lg:w-[31.5%]' />
              </div>
            </>
          )}
        </div>

        {hasMore && (
          <div className='flex justify-center mt-[40px] xl:mt-[60px]'>
            <button
              onClick={loadMore}
              disabled={loading}
              className='h-[48px] px-[32px] rounded-[8px] border border-accent text-accent hover:bg-accent hover:text-white transition-all duration-300 disabled:opacity-50'>
              {loading ? 'Загрузка...' : 'Загрузить ещё'}
            </button>
          </div>
        )}
      </div>
    </section>
  )
}
